import React, { useCallback, useEffect, useRef } from 'react';
import { isEmbeddedWebView } from '../utils/inAppBrowser';

/**
 * A decorative, looping <video> (loading animations, background footage) that
 * never shows browser playback chrome, in any browser.
 *
 * Leaving off `controls` isn't enough on its own. Browsers still draw their own
 * UI over a bare <video> in a handful of cases, each handled here:
 *
 *   - iOS Safari in Low Power Mode refuses autoplay and paints its big
 *     start-playback button over the frame instead
 *     → ::-webkit-media-controls-start-playback-button hidden
 *   - Chrome on Android overlays a play button when autoplay fails, and a Cast
 *     button on any video it thinks it can cast
 *     → ::-webkit-media-controls-overlay-play-button hidden, disableRemotePlayback
 *   - Android WebViews (the Instagram/Facebook/TikTok in-app browsers) show a
 *     grey play-icon poster on any video that doesn't set a poster of its own
 *     → a transparent poster by default; callers pass a real first frame
 *   - Safari's AirPlay and Picture-in-Picture affordances
 *     → x-webkit-airplay="deny", disablePictureInPicture
 *   - Right-click "Show controls" on desktop, long-press menus on phones
 *     → pointer-events: none (it's decoration; nothing to interact with)
 *   - TikTok's in-app browsers draw their own native player chrome over every
 *     <video> (scrubber, mute, fullscreen, a play button with ±10s skips) and
 *     sends it fullscreen on mount or tap — nothing on the page can hide it
 *     → no <video> at all there: `animated` (an animated WebP of the clip,
 *       made by scripts/animated-webp.py) or else the poster is rendered as
 *       an <img>, or nothing when there's neither (callers layer a still
 *       underneath). With `animated`, mounting is what starts the motion,
 *       so callers that play a clip on cue pass it only from that moment,
 *       and `onPlaying` fires once the image has loaded.
 *
 * With no controls there's also no play button to fall back on, so if the
 * video still isn't playing by the visitor's next tap or keypress anywhere on
 * the page, that gesture starts it (Low Power Mode honours a gesture) rather
 * than leaving the poster frozen on screen.
 *
 * NOT for anything the visitor is meant to watch and control — e.g. the
 * FunnelWaitVideo episode keeps its native controls.
 */

// 1x1 transparent GIF: stands in for the WebView's default play-icon poster.
const TRANSPARENT_POSTER =
  'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';

// Events that count as a user gesture for media playback. touchend rather
// than touchstart: WebKit only grants playback on the end of a touch.
const GESTURE_EVENTS = ['touchend', 'click', 'keydown'];

// `ref` (a React 19 prop) reaches the <video> too, for callers that start
// playback themselves — e.g. from a tap, which even Low Power Mode honours.
const NoControlsVideo = ({ className = '', poster, animated, controls: _controls, ref, ...props }) => {
  const videoRef = useRef(null);
  const { autoPlay } = props;
  const setRefs = useCallback(
    (node) => {
      videoRef.current = node;
      if (typeof ref === 'function') ref(node);
      else if (ref) ref.current = node;
    },
    [ref]
  );

  // Starting is left to the autoplay attribute — deliberately no play() call
  // on mount. Chrome aborts a scripted play() on a silent video in a hidden
  // tab ("video-only background media was paused to save power") and never
  // resumes it, whereas attribute autoplay picks back up when the tab is
  // shown. So opening the funnel in a background tab would have frozen it.
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !autoPlay) return undefined;

    const startOnGesture = () => {
      if (!video.paused) return;
      const attempt = video.play();
      // Older engines return undefined rather than a promise
      if (attempt?.catch) attempt.catch(() => {});
    };
    const disarm = () => {
      GESTURE_EVENTS.forEach((type) => document.removeEventListener(type, startOnGesture, true));
      video.removeEventListener('playing', disarm);
    };
    GESTURE_EVENTS.forEach((type) => document.addEventListener(type, startOnGesture, true));
    // Once it's playing, autoplay worked (or a gesture fixed it); stop listening
    video.addEventListener('playing', disarm);
    return disarm;
  }, [autoPlay]);

  // After the hooks, so the hook count never changes between renders
  if (isEmbeddedWebView()) {
    const still = animated || poster;
    if (!still) return null;
    const { style, role, 'aria-label': ariaLabel, 'aria-hidden': ariaHidden, onPlaying } = props;
    return (
      <>
        <NoControlsVideoStyles />
        <img
          className={`ncv-still ${className}`.trim()}
          src={still}
          alt={ariaLabel || ''}
          role={role}
          aria-hidden={ariaHidden}
          style={style}
          draggable="false"
          // An animated image is moving as soon as it has loaded
          onLoad={animated && onPlaying ? () => onPlaying() : undefined}
        />
      </>
    );
  }

  return (
    <>
      <NoControlsVideoStyles />
      <video
        ref={setRefs}
        className={`ncv-video ${className}`.trim()}
        poster={poster || TRANSPARENT_POSTER}
        playsInline
        webkit-playsinline=""
        muted
        disablePictureInPicture
        disableRemotePlayback
        x-webkit-airplay="deny"
        {...props}
      />
    </>
  );
};

// One rule per pseudo-element, never a combined selector list: a browser that
// doesn't recognise one pseudo-element drops the WHOLE rule it appears in.
const NoControlsVideoStyles = () => (
  <style>{`
    .ncv-video, .ncv-still { pointer-events: none; }
    .ncv-video::-webkit-media-controls { display: none !important; }
    .ncv-video::-webkit-media-controls-overlay-play-button { display: none !important; }
    .ncv-video::-webkit-media-controls-start-playback-button {
      display: none !important;
      -webkit-appearance: none;
    }
  `}</style>
);

export default NoControlsVideo;
